'use client'

import { Clock, Star } from 'lucide-react'
import Image from 'next/image'
import Link from 'next/link'
import { useCallback, useEffect, useState } from 'react'
import { getProductType } from '@/helpers/product-type'
import type { CourseType } from '@/types/course-type'
import { Button } from '@/components/ui/button'
import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel'

interface CourseCarouselProps {
  courses: CourseType[]
}

function getDestinationRoute(
  productType: string,
  productId: string,
  publicUrl: string
): string {
  const productRouteMap: Record<
    string,
    (productId: string, publicUrl: string) => string
  > = {
    course: (productId, publicUrl) => `/cursos/info/${productId}/${publicUrl}`,
    webinar: (productId, publicUrl) => `/cursos/info/${productId}/${publicUrl}`,
    mentorship: (productId, publicUrl) =>
      `/cursos/info/${productId}/${publicUrl}`,
    personalized: (productId, publicUrl) =>
      `/cursos/info/${productId}/${publicUrl}`,
    postgraduate: (productId, publicUrl) => `/pos-graduacoes/info/${productId}`,
    track: (_, publicUrl) => `/cursos/info/${productId}/${publicUrl}`,
  }

  const routeBuilder = productRouteMap[productType.toLowerCase()]
  return routeBuilder?.(productId, publicUrl) ?? '/cursos'
}

export default function CourseCarousel({ courses }: CourseCarouselProps) {
  const [api, setApi] = useState<CarouselApi>()
  const [currentIndex, setCurrentIndex] = useState(0)
  const [totalItems, setTotalItems] = useState(0)

  const handleScrollToIndex = useCallback(
    (index: number) => {
      api?.scrollTo(index)
    },
    [api]
  )

  useEffect(() => {
    if (!api) return

    const updateCarouselState = () => {
      setCurrentIndex(api.selectedScrollSnap())
      setTotalItems(api.scrollSnapList().length)
    }

    updateCarouselState()
    api.on('select', updateCarouselState)

    return () => {
      api.off('select', updateCarouselState)
    }
  }, [api])

  if (!courses || courses.length === 0) {
    return null
  }

  return (
    <section className="container pt-17 pb-12">
      <div className="mx-auto max-w-148 space-y-2 text-center md:space-y-4">
        <span className="font-medium text-blue-600 text-sm md:text-base">
          RECOMENDAÇÕES
        </span>
        <h2 className="font-bold text-3.5xl text-neutral-900 leading-10 md:text-4xl">
          Top Mais Procurados
        </h2>
        <p className="font-medium text-gray-500 text-sm md:text-lg">
          Confira abaixo a lista dos cursos mais buscados por profissionais
          espalhados por todo o país
        </p>
      </div>

      <div className="mt-6">
        <div className="mb-8 flex items-center justify-between">
          <div className="flex-1" />

          <div className="flex items-center space-x-2">
            {Array.from({ length: totalItems }).map((_, index) => (
              <button
                aria-label={`Ir para slide ${index + 1}`}
                className={`h-3 w-3 rounded-full transition-colors ${
                  currentIndex === index ? 'bg-blue-600' : 'bg-gray-300'
                }`}
                key={index}
                onClick={() => handleScrollToIndex(index)}
                type="button"
              />
            ))}
          </div>

          <div className="flex flex-1 justify-end">
            <Button
              asChild
              className="max-w-40 border-blue-600 px-3 py-1 text-blue-600 text-xs hover:bg-blue-50"
              size="sm"
              variant="outline"
            >
              <Link className="font-semibold" href="/cursos">
                VER TODOS OS CURSOS
              </Link>
            </Button>
          </div>
        </div>

        <div className="relative">
          <Carousel
            className="w-full"
            opts={{
              align: 'start',
              loop: true,
            }}
            setApi={setApi}
          >
            <CarouselContent className="-ml-4">
              {courses.map(course => {
                const routeDestination = getDestinationRoute(
                  course.product_type,
                  course.external_id ?? course.reference,
                  course.public_url
                )

                const productType = getProductType(course.product_type)
                const workloadText =
                  course.workloads && course.workloads.length > 1
                    ? `${course.workloads[0]?.workload_value} a ${course.workloads[course.workloads.length - 1]?.workload_value} Horas`
                    : `${course.workloads[0]?.workload_value} Horas`

                return (
                  <CarouselItem
                    className="pl-4 md:basis-1/2 lg:basis-1/3"
                    key={course.reference}
                  >
                    <Link
                      className="group block w-full"
                      href={routeDestination}
                      title={course.name}
                    >
                      <div className="relative w-full overflow-hidden rounded-lg border border-gray-200 bg-white shadow-xs transition-shadow hover:shadow-md">
                        <div className="relative">
                          <Image
                            alt={course.name}
                            className="h-48 w-full object-cover transition-transform duration-300 group-hover:scale-105"
                            height={180}
                            src={
                              course.image_url ?? '/img/product-fallback.webp'
                            }
                            width={280}
                          />
                          <span className="absolute top-2 left-4 z-10 rounded bg-[#EAEEFD] px-2 py-1 font-medium text-gray-950 text-xs">
                            {productType}
                          </span>
                        </div>

                        <div className="space-y-3 p-4">
                          <h3 className="line-clamp-2 font-bold text-base text-gray-900 leading-5 transition-colors group-hover:text-blue-600">
                            {course.name}
                          </h3>

                          <div className="flex items-center justify-between text-gray-600 text-sm">
                            <div className="flex items-center space-x-1">
                              <Star className="h-4 w-4 fill-current text-yellow-400" />
                              <span>4.8</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <Clock className="h-4 w-4 text-gray-400" />
                              <span>{workloadText}</span>
                            </div>
                          </div>

                          <Button
                            className="w-full bg-blue-600 font-semibold text-white hover:bg-blue-700"
                            size="sm"
                          >
                            FAZER INSCRIÇÃO
                          </Button>
                        </div>
                      </div>
                    </Link>
                  </CarouselItem>
                )
              })}
            </CarouselContent>

            <CarouselPrevious className="-left-12 -translate-y-1/2 absolute top-1/2 border-gray-300 bg-white hover:bg-gray-50" />
            <CarouselNext className="-right-12 -translate-y-1/2 absolute top-1/2 border-gray-300 bg-white hover:bg-gray-50" />
          </Carousel>
        </div>
      </div>
    </section>
  )
}
